/**
 * 查找指定节点id在节点树上的全路径
 * @param {any} tree 根节点对象
 * @param {any} nodeId 查找节点id
 * @returns {Array}
 */
export function findNodeFullPath(tree, nodeId) {
  const paths = [];
  // 递归查找路径
  function findNode(node) {
    paths.push({
      parentId: node.parentId,
      id: node.id,
      name: node.name,
      hasChildren: node.children && node.children.length > 0,
    });
    // 找到符合条件的节点，通过throw终止掉递归
    if (node.id === nodeId) {
      throw new Error('已找到当前节点的完整节点路径');
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        findNode(node.children[i]);
      }
      // 当前节点的子节点遍历完依旧没找到，则删除路径中的该节点
      paths.pop();
    } else {
      // 无子节点则删除路径中的该节点
      paths.pop();
    }
  }
  try {
    findNode(tree);
    return paths;
  } catch (err) {
    console.log(err.message);
    return paths;
  }
}

/**
 * 格式化树数据
 * 1. 公司或部门添加字段：isOpen-是否展开节点
 * 2. 用户添加字段: checked-是否选中
 * @param {any} tree 根节点对象
 * @returns {undefined}
 */
export function formatTree(tree) {
  function addOpen(node) {
    if (node.parentId === 0) {
      node.isOpen = true;
    } else {
      // type: 'company'|'dept'|'user'
      if (node.type !== 'user') {
        node.isOpen = false;
      } else {
        node.checked = false;
      }
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        addOpen(node.children[i]);
      }
    }
  }
  addOpen(tree);
}

/**
 * 格式化树数据：添加层级字段level，通过父级level传递给子级递归计算
 * @param {any} tree 根节点对象
 * @returns {undefined}
 */
export function formatTreeLevel(tree) {
  function getLevel(node, level = 1) {
    node.level = level;
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        getLevel(node.children[i], node.level + 1);
      }
    }
  }
  getLevel(tree);
}

/**
 * 查找当前节点的下一级子节点
 * @param {any} tree 根节点对象
 * @param {any} nodeId 查找节点id
 * @returns {Array}
 */
export function findSubNodes(tree, nodeId) {
  let subNodes = [];
  function findNodes(node) {
    if (node.id === nodeId && node.children && node.children.length > 0) {
      subNodes = node.children.map(({ id, name, userCount, children = [] }) => ({
        id,
        name,
        userCount,
        hasChildren: node.children && node.children.length > 0,
      }));
      throw new Error('已找到当前节点的子节点');
    } else if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        findNodes(node.children[i]);
      }
    }
  }
  try {
    findNodes(tree);
    return subNodes;
  } catch (err) {
    console.log(err.message);
    return subNodes;
  }
}

/**
 * 查找已勾选的子节点（部门下的用户节点）
 * @param {any} tree 根节点对象
 * @returns {Array}
 */
export function findCheckedNodes(tree) {
  const checkedList = [];
  function findNode(node) {
    if (node.type === 'user' && node.checked) {
      checkedList.push({
        id: node.id,
        name: node.name,
      });
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        findNode(node.children[i]);
      }
    }
  }
  findNode(tree);
  return checkedList;
}

/**
 * 更新用户节点的勾选状态
 * @param {any} tree 根节点对象
 * @param {any} { ids, isChecked } ids-要更新的节点列表 isChecked-节点勾选状态
 */
export function updateNodesChecked(tree, { ids, isChecked }) {
  function updateOpen(node) {
    if (node.type === 'user' && ids.includes(node.id)) {
      node.checked = isChecked;
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        updateOpen(node.children[i]);
      }
    }
  }
  updateOpen(tree);
}

/**
 * 更新节点的展开、收起状态
 * @param {any} tree 根节点对象
 * @param {any} { id, isOpen }
 * @returns {Array}
 */
export function updateNodeOpen(tree, { id, isOpen }) {
  function updateOpen(node) {
    if (node.id === id) {
      node.isOpen = isOpen;
      throw new Error(`已找到节点并更新了isOpen为${isOpen}`);
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        updateOpen(node.children[i]);
      }
    }
  }
  try {
    updateOpen(tree);
  } catch (err) {
    console.log(err.message);
  }
}

/**
 * 更新当前节点全路径的展开、收起状态
 * @param {any} tree 根节点对象
 * @param {any} { id, isOpen } id-当前节点id, isOpen-是否展开
 */
export function upateNodePathsOpen(tree, { id, isOpen }) {
  // 查找节点在节点树上的全路径
  const nodePaths = findNodeFullPath(tree, id);
  const nodeIds = nodePaths.map(item => item.id);
  // 递归更新isOpen
  function updateOpen(node) {
    if (nodeIds.includes(node.id)) {
      node.isOpen = isOpen;
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        updateOpen(node.children[i]);
      }
    }
  }
  updateOpen(tree);
}

/**
 * 更新节点全路径上每个节点的用户数
 * @param {any} tree 根节点对象
 * @param {any} { id, userCount } id-当前节点 userCount-用户数
 */
export function upateNodePathsUserCount(tree, { id, userCount }) {
  // 查找节点在节点树上的全路径
  const nodePaths = findNodeFullPath(tree, id);
  const nodeIds = nodePaths.map(item => item.id);
  // 递归更新userCount
  function updateOpen(node) {
    if (nodeIds.includes(node.id)) {
      node.userCount = node.userCount + userCount;
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        updateOpen(node.children[i]);
      }
    }
  }
  updateOpen(tree);
}

/**
 * 新增子节点
 * @param {any} tree 根节点对象
 * @param {any} node 节点信息
 */
export function addSubNode(tree, newNode) {
  function addNode(node) {
    if (node.id === newNode.parentId) {
      // 补全默认字段
      const tempNode = {
        ...newNode,
        level: node.level + 1,
        isOpen: false,
        children: [],
        userCount: 0,
      };
      if (node.children) {
        node.children.push(tempNode);
      } else {
        node.children = [tempNode];
      }
      throw new Error('已新增子节点');
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        addNode(node.children[i]);
      }
    }
  }
  try {
    addNode(tree);
  } catch (err) {
    console.log(err.message);
  }
}

/**
 * 删除节点：
 * 1. 删除节点
 * 2. 更新全部父级节点用户数
 * @param {any} tree 根节点对象
 * @param {any} { parentId, id } parentId-父节点 id-当前节点
 */
export function deleteNode(tree, { parentId, id }) {
  let userCount = 0;
  // 删除节点和记录节点的用户数
  function toDelete(node) {
    if (node.id === parentId) {
      // 查找当前节点的用户数
      userCount = node.children.find(item => item.id === id).userCount;
      // 过滤当前节点
      node.children = node.children.filter(item => item.id !== id);
      throw new Error('已删除节点');
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        toDelete(node.children[i]);
      }
    }
  }
  try {
    toDelete(tree);
  } catch (err) {
    console.log(err.message);
  }
  // 更新父路径上节点用户数
  upateNodePathsUserCount(tree, { id: parentId, userCount: -1 * userCount });
}

/**
 * 更新部门: 部门名称、上级部门
 * 1. 在原节点位置移除
 * 2. 在新节点位置添加
 * 3. 移除后在父路径上减少用户数，添加后在父路径上增加用户数
 * 4. 更新移动节点的层级
 * 5. 在新的添加位置展开父级全路径
 * @param {any} tree 根节点对象
 * @param {any} { deptId, deptName, newParentDeptId, oldParentDeptId }
 */
export function updateNode(tree, { deptId, deptName, newParentDeptId, oldParentDeptId }) {
  // 当前移动的节点
  let currNode = {};
  // 删除节点和记录节点
  function toDelete(node) {
    if (node.id === oldParentDeptId) {
      // 查找当前节点
      currNode = node.children.find(item => item.id === deptId);
      currNode.name = deptName;
      // 过滤当前节点
      node.children = node.children.filter(item => item.id !== deptId);
      throw new Error('已从原位置移除节点');
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        toDelete(node.children[i]);
      }
    }
  }
  // 在新的父节点插入子节点
  function toInsert(node) {
    if (node.id === newParentDeptId) {
      if (node.children) {
        node.children.push(currNode);
      } else {
        node.children = [currNode];
      }
      throw new Error('已在新增位置插入节点');
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        toInsert(node.children[i]);
      }
    }
  }
  try {
    toDelete(tree);
  } catch (err) {
    console.log(err.message);
  }
  try {
    toInsert(tree);
  } catch (err) {
    console.log(err.message);
  }
  // 减少原父路径上节点用户数
  upateNodePathsUserCount(tree, { id: oldParentDeptId, userCount: -1 * currNode.userCount });
  // 增加新父路径上节点用户数
  upateNodePathsUserCount(tree, { id: newParentDeptId, userCount: currNode.userCount });
  // 更新树层级计算
  formatTreeLevel(tree);
  // 展开新的父级全路径
  upateNodePathsOpen(tree, { id: newParentDeptId, isOpen: true });
}

/**
 * 更新当前节点的子节点顺序
 * @param {any} tree 根节点对象
 * @param {any} { parentId, orderIds } parentId-当前节点id, orderIds-子节点id顺序数组
 */
export function updateSubNodeOrder(tree, { parentId, orderIds }) {
  function updateOrder(node) {
    if (node.id === parentId) {
      node.children = orderIds.map(orderId => node.children.find(item => item.id === orderId));
      throw new Error('已更新当前节点的子节点顺序：', parentId);
    }
    if (node.children && node.children.length > 0) {
      for (let i = 0, len = node.children.length; i < len; i++) {
        updateOrder(node.children[i]);
      }
    }
  }
  try {
    updateOrder(tree);
  } catch (err) {
    console.log(err.message);
  }
}
